<script>
/**
 * @component PacientesTabla
 * @description Tabla de pacientes del rediseño del tablero de seguimiento
 *              (UC-REG-065). Bloque B del layout. Densa, sortable, con
 *              métricas agregadas: semáforo, alertas, adherencia 7d/30d con
 *              tendencia, último score IA, signals destacadas.
 *
 *              Lazy-load por fila: al renderizar, hace un GET
 *              /patients/:id/monitoring para hidratar adherencia + score IA
 *              + signals. Si el endpoint es lento, las celdas muestran "—"
 *              con spinner hasta que llegue.
 *
 * @example <PacientesTabla :patients="patients" @ver="onVerPaciente" />
 */
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import monitoringService, { trendIndicator } from "@/services/monitoring";

export default {
  name: "PacientesTabla",

  props: {
    /**
     * @description Array de pacientes (de patientService.getAggregates).
     *              Cada paciente: { _id, firstName, lastName, age, patient.aggregates }.
     */
    patients: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // Hidratación lazy por patientId: { [patientId]: { adherence, lastConv, loading } }
      hydration: {},
    };
  },

  mounted() {
    this.hydrateAll();
  },

  watch: {
    patients() {
      this.hydrateAll();
    },
  },

  methods: {
    /**
     * @description Dispara fetch de monitoring data para cada paciente visible.
     *              Concurrencia limitada a 5 paralelos para no saturar el API.
     */
    async hydrateAll() {
      const newHydration = { ...this.hydration };
      const toFetch = this.patients.filter((p) => !newHydration[p._id]);
      for (const p of toFetch) {
        newHydration[p._id] = {
          loading: true,
          adherence: null,
          lastConv: null,
        };
      }
      this.hydration = newHydration;

      // Concurrency 5
      const queue = [...toFetch];
      const workers = Array.from({ length: 5 }, async () => {
        while (queue.length > 0) {
          const p = queue.shift();
          if (!p) return;
          try {
            const { data } = await monitoringService.getPatientMonitoring(
              p._id
            );
            this.$set(this.hydration, p._id, {
              loading: false,
              adherence: data.adherence,
              lastConv: data.lastConversation,
            });
          } catch (e) {
            this.$set(this.hydration, p._id, {
              loading: false,
              adherence: null,
              lastConv: null,
              error: true,
            });
          }
        }
      });
      await Promise.all(workers);
    },
    /**
     * @description Avatar con iniciales del paciente.
     */
    initials(p) {
      const f = (p.firstName || "?").charAt(0).toUpperCase();
      const l = (p.lastName || "").charAt(0).toUpperCase();
      return f + l;
    },
    /**
     * @description Devuelve el semáforo agregado del paciente desde aggregates.
     *              Worst color among red > yellow > green.
     */
    patientSemaphore(p) {
      const ag = p.patient && p.patient.aggregates;
      if (!ag) return "gray";
      if (ag.red > 0) return "red";
      if (ag.yellow > 0) return "yellow";
      if (ag.green > 0 || ag.incomplete > 0) return "green";
      return "gray";
    },
    /**
     * @description Cuenta de alertas activas (rojas + amarillas sin resolver).
     */
    alertCount(p) {
      const ag = p.patient && p.patient.aggregates;
      if (!ag) return 0;
      return (ag.red || 0) + (ag.yellow || 0);
    },
    /**
     * @description Label médico (concatenación de doctors[]).
     */
    doctorLabel(p) {
      if (!p.doctors || !p.doctors.length) return "—";
      return p.doctors
        .map((d) => `${d.firstName || ""} ${d.lastName || ""}`.trim())
        .join(", ");
    },
    /**
     * @description Tendencia con flecha y color desde delta numérico.
     */
    trendInfo(delta) {
      return trendIndicator(delta);
    },
    /**
     * @description Última actividad relativa.
     */
    lastActivity(p) {
      const at = p.patient?.aggregates?.updatedAt;
      if (!at) return "—";
      return "hace " + formatDistanceToNow(new Date(at), { locale: es });
    },
    onVer(p) {
      this.$emit("ver", p);
    },
  },
};
</script>

<template lang="pug">
  .pacientes-tabla
    el-table(:data="patients" border style="width: 100%" @row-click="onVer")
      el-table-column(label="Paciente" min-width="200")
        template(slot-scope="scope")
          .patient-cell
            span.patient-avatar {{ initials(scope.row) }}
            .patient-info
              span.patient-name {{ scope.row.firstName }} {{ scope.row.lastName }}
              span.patient-meta {{ scope.row.age ? `${scope.row.age} años` : "" }}{{ scope.row.age ? " · " : "" }}{{ doctorLabel(scope.row) }}

      el-table-column(label="Semáforo" width="120")
        template(slot-scope="scope")
          span.badge(:class="`badge--${patientSemaphore(scope.row)}`")
            span.badge__dot
            span {{ patientSemaphore(scope.row) === "gray" ? "—" : patientSemaphore(scope.row).charAt(0).toUpperCase() + patientSemaphore(scope.row).slice(1) }}

      el-table-column(label="Alertas" width="90" align="center")
        template(slot-scope="scope")
          span.alert-count(:class="{ 'alert-count--zero': !alertCount(scope.row) }") {{ alertCount(scope.row) }}

      el-table-column(label="Adherencia" width="160")
        template(slot-scope="scope")
          .adherence(v-if="hydration[scope.row._id] && hydration[scope.row._id].adherence")
            .adherence__line
              span.adherence__value {{ hydration[scope.row._id].adherence.sevenDays.percentage != null ? hydration[scope.row._id].adherence.sevenDays.percentage + "%" : "—" }}
              span.arrow(:class="trendInfo(hydration[scope.row._id].adherence.sevenDays.trend).className") {{ trendInfo(hydration[scope.row._id].adherence.sevenDays.trend).symbol }}
              span.adherence__period 7d
            .adherence__line
              span.adherence__value {{ hydration[scope.row._id].adherence.thirtyDays.percentage != null ? hydration[scope.row._id].adherence.thirtyDays.percentage + "%" : "—" }}
              span.arrow(:class="trendInfo(hydration[scope.row._id].adherence.thirtyDays.trend).className") {{ trendInfo(hydration[scope.row._id].adherence.thirtyDays.trend).symbol }}
              span.adherence__period 30d
          .adherence(v-else)
            i.el-icon-loading(v-if="hydration[scope.row._id] && hydration[scope.row._id].loading")
            span(v-else) —

      el-table-column(label="Score IA" width="130")
        template(slot-scope="scope")
          .score(v-if="hydration[scope.row._id] && hydration[scope.row._id].lastConv")
            span.score__value {{ hydration[scope.row._id].lastConv.score }}/10
            span.score__template {{ hydration[scope.row._id].lastConv.name }}
          .score(v-else)
            span(v-if="hydration[scope.row._id] && hydration[scope.row._id].loading")
              i.el-icon-loading
            span(v-else) —

      el-table-column(label="Signals" min-width="160")
        template(slot-scope="scope")
          .signals(v-if="hydration[scope.row._id] && hydration[scope.row._id].lastConv && hydration[scope.row._id].lastConv.topSignals && hydration[scope.row._id].lastConv.topSignals.length")
            span.signal-chip(
              v-for="(s, idx) in hydration[scope.row._id].lastConv.topSignals"
              :key="`sig-${scope.row._id}-${idx}`"
              :class="`signal-chip--${s.severity}`"
            ) {{ s.criterion }}
          span.placeholder(v-else) —

      el-table-column(label="Última act." width="120")
        template(slot-scope="scope")
          span.last-activity {{ lastActivity(scope.row) }}

      el-table-column(label="" width="80" align="center")
        template(slot-scope="scope")
          el-button(type="text" @click.stop="onVer(scope.row)") Ver →
</template>

<style lang="scss" scoped>
.pacientes-tabla {
  font-family: "Plus Jakarta Sans", sans-serif;
  ::v-deep .el-table th {
    background: #fbfcfe;
    color: #6b7280;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  ::v-deep .el-table td {
    padding: 12px 0;
  }
  ::v-deep .el-table tr {
    cursor: pointer;
  }
}

.patient-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.patient-avatar {
  width: 32px;
  height: 32px;
  background: #e8eef7;
  color: #1d3557;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
}
.patient-info {
  display: flex;
  flex-direction: column;
}
.patient-name {
  font-weight: 600;
  color: #0f1e36;
  font-size: 13px;
}
.patient-meta {
  font-size: 11px;
  color: #9ca3af;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 16px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid;
}
.badge--green {
  color: #1f8a4c;
  background: #ebf7f0;
  border-color: #b9e3c7;
}
.badge--yellow {
  color: #b07c0a;
  background: #fbf3e0;
  border-color: #f1d899;
}
.badge--red {
  color: #b3261e;
  background: #fbe9e7;
  border-color: #efb4b0;
}
.badge--gray {
  color: #6b7280;
  background: #f3f4f6;
  border-color: #e5e7eb;
}
.badge__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.alert-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 26px;
  background: #fbe9e7;
  color: #b3261e;
  border-radius: 13px;
  font-size: 12px;
  font-weight: 700;
  &--zero {
    background: #f3f4f6;
    color: #9ca3af;
  }
}

.adherence {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.adherence__line {
  display: flex;
  gap: 8px;
  align-items: baseline;
}
.adherence__value {
  font-weight: 700;
  color: #0f1e36;
  font-variant-numeric: tabular-nums;
  font-size: 13px;
}
.adherence__period {
  font-size: 10px;
  color: #9ca3af;
}
.arrow {
  font-size: 11px;
  font-weight: 700;
}
.arrow.trend--up {
  color: #1f8a4c;
}
.arrow.trend--down {
  color: #b3261e;
}
.arrow.trend--flat {
  color: #9ca3af;
}
.arrow.trend--none {
  color: #9ca3af;
}

.score {
  display: flex;
  flex-direction: column;
}
.score__value {
  font-weight: 700;
  font-size: 13px;
  color: #0f1e36;
}
.score__template {
  font-size: 10px;
  color: #9ca3af;
  font-weight: 500;
  margin-top: 2px;
}

.signals {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.signal-chip {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #f3f4f6;
  color: #6b7280;
  font-weight: 600;
}
.signal-chip--high {
  background: #fbe9e7;
  color: #b3261e;
}
.signal-chip--mid {
  background: #fbf3e0;
  color: #b07c0a;
}
.signal-chip--low {
  background: #ebf7f0;
  color: #1f8a4c;
}

.last-activity {
  font-size: 11px;
  color: #6b7280;
}
.placeholder {
  color: #9ca3af;
}
</style>
